import { useKeyboardContext } from "@/core/keyboard";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAppSelector } from "@/hooks/useAppSelector";
import { usePosSession } from "@/hooks/usePosSession";
import { usePosHardware } from "@/pages/pos/PosHardwareContext";

function statusDot(ok: boolean) {
  return ok ? "text-emerald-400" : "text-slate-500";
}

function StatusItem({
  label,
  hint,
  active,
}: {
  label: string;
  hint: string;
  active: boolean;
}) {
  return (
    <Tooltip content={hint}>
      <span className={statusDot(active)}>{label}</span>
    </Tooltip>
  );
}

export function PosStatusBar() {
  const { focusZone, statusHint } = useKeyboardContext();
  const store = useAppSelector((s) => s.app.selectedStore);
  const user = useAppSelector((s) => s.auth.user);
  const { activeShiftId } = usePosSession();
  const { config, prefs, barcodePrinterReady } = usePosHardware();

  const scaleOn = !!config?.scaleEnabled && !!prefs.scaleConnected;
  const labelOn = barcodePrinterReady;
  const receiptOn =
    !!config?.thermalPrinterEnabled && !!prefs.receiptPrinterConnected;
  const displayOn = !!config?.customerDisplayEnabled;

  return (
    <footer className="flex h-9 shrink-0 items-center justify-between border-t border-slate-300 bg-slate-800 px-3 text-xs text-slate-200">
      <div className="flex items-center gap-4">
        <span className="font-medium text-emerald-400">● Billing</span>
        <span className={activeShiftId ? "text-emerald-400" : "text-amber-400"}>
          {activeShiftId ? "Shift open" : "No shift"}
        </span>
        <span className="text-slate-400">Focus:</span>
        <span className="capitalize text-white">{focusZone}</span>
        {statusHint ? (
          <span className="hidden truncate text-slate-300 lg:inline max-w-md">
            {statusHint}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3 text-slate-400">
        <span className="hidden md:flex items-center gap-2">
          <StatusItem label="Scan" hint="Barcode scanner (USB HID)" active />
          {config?.scaleEnabled && (
            <StatusItem label="Scale" hint="Weighing scale" active={scaleOn} />
          )}
          {config?.barcodePrinterEnabled && (
            <StatusItem label="Label" hint="Label printer" active={labelOn} />
          )}
          {config?.thermalPrinterEnabled !== false && (
            <StatusItem
              label="Receipt"
              hint="Receipt printer"
              active={receiptOn}
            />
          )}
          {displayOn && (
            <StatusItem
              label="Display"
              hint="Customer display"
              active={displayOn}
            />
          )}
        </span>
        <span>
          {store?.name} · {user?.firstName}
        </span>
        <span className="hidden sm:inline text-slate-400">
          Open Guide for keyboard shortcuts
        </span>
      </div>
    </footer>
  );
}
